/**
 * Landing v3 — the cards' hover micro-interaction, shared by the landing's
 * tiles and the About page's cards (user 2026-09-25).
 */
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';

import type { BadgeHue } from './landing-hues';

/** How far a card leans toward the cursor at its edge. */
const PAN_TILT_DEG = 5;
const PAN_SPRING = { stiffness: 260, damping: 26, mass: 0.6 } as const;

/**
 * Every card's hover (user 2026-09-25): under a mouse it leans a few degrees
 * toward the cursor and a soft light in the card's OWN accent follows it (the
 * hue of its badge — user: "not always primary violet"); the layer inside (a
 * photo, an icon) pans with it for depth. Sprung, so it eases in and settles
 * back when the cursor leaves. Mouse only — a finger scrolls — and off under
 * reduced motion. `x` / `y` run −0.5…0.5 across the card.
 */
export function useCardPan(
  reduceMotion: boolean,
  {
    hue = 'primary',
    tiltDeg = PAN_TILT_DEG,
    lightAlpha = 0.14,
  }: {
    hue?: BadgeHue;
    /** The lean at the card's edge. */
    tiltDeg?: number;
    /** The light's strength at its centre. */
    lightAlpha?: number;
  } = {},
) {
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  const on = useMotionValue(0);
  const x = useSpring(px, PAN_SPRING);
  const y = useSpring(py, PAN_SPRING);
  const lit = useSpring(on, PAN_SPRING);
  const rotateY = useTransform(x, (v) => v * tiltDeg);
  const rotateX = useTransform(y, (v) => -v * tiltDeg);
  const lightX = useTransform(x, (v) => (v + 0.5) * 100);
  const lightY = useTransform(y, (v) => (v + 0.5) * 100);
  // The DS triplet, so the light follows the theme like the badge does.
  const tint = `rgba(var(--${hue}-500-rgb), ${lightAlpha})`;
  const light = useMotionTemplate`radial-gradient(280px circle at ${lightX}% ${lightY}%, ${tint}, transparent 70%)`;
  const handlers = reduceMotion
    ? {}
    : {
        onPointerMove: (e: React.PointerEvent<HTMLElement>) => {
          if (e.pointerType !== 'mouse') return;
          const r = e.currentTarget.getBoundingClientRect();
          px.set((e.clientX - r.left) / r.width - 0.5);
          py.set((e.clientY - r.top) / r.height - 0.5);
          on.set(1);
        },
        onPointerLeave: () => {
          px.set(0);
          py.set(0);
          on.set(0);
        },
      };
  return {
    handlers,
    x,
    y,
    tilt: { rotateX, rotateY, transformPerspective: 900 },
    light: (
      <motion.span
        aria-hidden
        style={{ background: light, opacity: lit }}
        className="pointer-events-none absolute inset-0 rounded-[inherit]"
      />
    ),
  };
}
